/// <amd-module name="Artifacts/Feed/services/CreateFeedService" />

import { loadNonNullData } from "Artifacts/Root/helpers/DataProviderHelpers";
import {
  getTfsProject,
  isCurrentSelectedProjectPublic,
} from "Artifacts/Root/helpers/ProjectHelpers";
import { IdentityDataService } from "Artifacts/Root/services/IdentityDataService";
import { CreateFeedAdditionalData } from "Artifacts/Root/types/AzureArtifacts";
import {
  Feed,
  FeedPermission,
  FeedRole,
  FeedView,
  FeedVisibility,
} from "Artifacts/Root/web-apis/contracts/Feed";
import { getFeedClient, IFeedRestClient } from "Artifacts/Root/web-apis/RestClient/Feed";
import { IVssService, Services, VssService } from "VSS/Platform/Context";
import { Identity, IdentityDescriptor } from "VSS/Platform/Contracts/Identities";

export interface ICreateFeedOptions {
  name: string;
  visibility: string;
  includePublicUpstreams: boolean;
  scope: string;
}

export enum Visibility {
  AadTenant = "aadTenant",
  Collection = "collection",
  Private = "private",
  Public = "public",
  Project = "project",
}

export enum Scope {
  Project = "project",
  Organization = "org",
}

export interface ICreateFeedService extends IVssService {
  createFeed(
    feedData: ICreateFeedOptions,
    feedServiceParams: CreateFeedAdditionalData,
  ): Promise<Feed>;
  getCreateFeedServiceParams(): Promise<CreateFeedAdditionalData | undefined>;
}

export class CreateFeedService extends VssService implements ICreateFeedService {
  private _feedRestClient: IFeedRestClient;
  private feedServiceParams: CreateFeedAdditionalData | undefined;

  public async createFeed(
    feedData: ICreateFeedOptions,
    feedServiceParams: CreateFeedAdditionalData,
  ): Promise<Feed> {
    const projectId =
      feedData.scope === Scope.Project ? getTfsProject(this.pageContext)?.id : undefined;
    const isFeedPublic = projectId && isCurrentSelectedProjectPublic(this.pageContext);
    const feedPermissions = await this.getPermissions(
      feedData.visibility,
      feedServiceParams.collectionBuildIdentity,
      feedServiceParams.projectBuildIdentity,
      feedServiceParams.everyoneGroupIdentityDescriptor,
      projectId,
    );

    const newFeed = {
      name: feedData.name,
      hideDeletedPackageVersions: true,
      permissions: feedPermissions,
      upstreamEnabled: !isFeedPublic,
      project: projectId ? { id: projectId } : undefined,
      upstreamSources:
        !isFeedPublic && feedData.includePublicUpstreams
          ? feedServiceParams.defaultPublicUpstreams
          : [],
    };

    this._feedRestClient = getFeedClient(this.pageContext);

    const createdFeed = await this._feedRestClient.createFeed(newFeed as Feed, projectId);

    // Once feed is created, need to update the view
    const updatedFeed = await this.updateViewPermissions(
      createdFeed,
      feedData.visibility,
      feedServiceParams.everyoneGroupIdentityDescriptor,
    );

    return updatedFeed;
  }

  private async getPermissions(
    visibility: string,
    collectionBuildIdentity: Identity,
    projectBuildIdentity: Identity,
    everyoneGroupIdentityDescriptor: IdentityDescriptor,
    projectId?: string,
  ): Promise<FeedPermission[]> {
    const permissions: FeedPermission[] = [];

    // Add Project Build Identity
    if (projectId) {
      projectBuildIdentity?.descriptor &&
        permissions.push({
          identityDescriptor: projectBuildIdentity.descriptor,
          identityId: projectBuildIdentity.id,
          role: FeedRole.Collaborator,
        } as FeedPermission);

      const identityDataService: IdentityDataService = this.pageContext.getService(
        "IIdentityDataService",
      );
      const projectIdentityGroups: Identity[] = projectId
        ? await identityDataService.listGroups(projectId)
        : [];

      // Add Project Administrators
      const projectAdministratorsGroup = projectIdentityGroups.filter(
        (i) => i.properties.Account.$value === "Project Administrators",
      );
      projectAdministratorsGroup.forEach((identity) => {
        permissions.push({
          identityDescriptor: identity.descriptor,
          identityId: identity.id,
          role: FeedRole.Administrator,
        } as FeedPermission);
      });

      // Add Project Contributors
      const projectContributorsGroup = projectIdentityGroups.filter(
        (i) => i.properties.Account.$value === "Contributors",
      );
      projectContributorsGroup.forEach((identity) => {
        permissions.push({
          identityDescriptor: identity.descriptor,
          identityId: identity.id,
          role: FeedRole.Contributor,
        } as FeedPermission);
      });
    } else {
      // Only Necessary in the case of an Org-scoped Feed
      permissions.push({
        // Add Collection Build Identity
        identityDescriptor: collectionBuildIdentity.descriptor,
        identityId: collectionBuildIdentity.id,
        role: FeedRole.Collaborator,
      } as FeedPermission);
    }

    // Add EveryoneGroup as a reader if project is public
    if (visibility === Visibility.Public && everyoneGroupIdentityDescriptor) {
      permissions.push({
        identityDescriptor: everyoneGroupIdentityDescriptor,
        role: FeedRole.Reader,
      } as FeedPermission);
    }

    return permissions;
  }

  public async getCreateFeedServiceParams(): Promise<CreateFeedAdditionalData | undefined> {
    if (!this.feedServiceParams) {
      const params = await loadNonNullData<CreateFeedAdditionalData>(
        this.pageContext,
        "ms.azure-artifacts.create-feed-data-provider",
      );
      this.feedServiceParams = params;
    }

    return this.feedServiceParams;
  }

  private async updateViewPermissions(
    createdFeed: Feed,
    visibility: string,
    everyoneGroupIdentityDescriptor: IdentityDescriptor,
  ): Promise<Feed> {
    if (visibility === Visibility.Private) {
      return createdFeed;
    }

    const projectId = createdFeed.project ? createdFeed.project.id : undefined;

    // Update the feedview to be public
    if (visibility === Visibility.Public) {
      const feedperms: FeedPermission[] = [
        {
          identityDescriptor: everyoneGroupIdentityDescriptor,
          role: FeedRole.Reader,
        } as FeedPermission,
      ];

      // For the specific view.
      const feedId = createdFeed.id + "@" + createdFeed.defaultViewId;

      await this._feedRestClient.setFeedPermissions(feedperms, feedId, projectId);
    }

    // Case: Update view visibility
    else if (visibility === Visibility.Collection || visibility === Visibility.AadTenant) {
      let visibilityEnum = FeedVisibility.Private;

      if (visibility === Visibility.AadTenant) {
        visibilityEnum = FeedVisibility.AadTenant;
      } else if (visibility === Visibility.Collection) {
        visibilityEnum = FeedVisibility.Collection;
      }

      const viewPatch: FeedView = { visibility: visibilityEnum } as FeedView;

      await this._feedRestClient.updateFeedView(
        viewPatch,
        createdFeed.id,
        createdFeed.defaultViewId,
        projectId,
      );
    }

    return await this._feedRestClient.getFeed(createdFeed.id, projectId);
  }
}

Services.add("ICreateFeedService", { serviceFactory: CreateFeedService });
